(() => {
  const choices = {
    project: ['Kitchen', 'Furniture', 'Bathroom or laundry', 'Caravan or motorhome', 'Shop or commercial', 'Not sure yet'],
    timing: ['Within a month', '1-3 months', 'Later', 'Just browsing'],
    installer: ['Me - DIY', 'A tradie or installer', "I'm in the trade or business"]
  };

  const consentVersion = 'velven-welcome-2026-11-02';
  function normaliseEmail(value) {
    const email = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (!email || email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('Enter a valid email address.');
    return email;
  }
  function validateAnswers(answers) {
    return Object.fromEntries(Object.entries(choices).map(([key, values]) => {
      if (!values.includes(answers?.[key])) throw new Error('Choose one answer in each group.');
      return [key, answers[key]];
    }));
  }

  const consentText = 'By continuing, you agree to receive Velven marketing emails. Unsubscribe anytime.';
  const nativeSessionKey = 'velven_native_lead_preview';
  function nativeLeadTags(payload, answers) {
    const tags = ['newsletter', 'velven-lead-popup', 'lead-request:' + payload.requestId, 'lead-consent-version:' + consentVersion];
    const fields = { 'consent-text': consentText, 'source-path': payload.sourcePage, ...payload.attribution };
    for (const [key, value] of Object.entries(fields)) {
      const encoded = encodeURIComponent(value);
      const chunks = encoded.match(/.{1,160}/g) || [''];
      chunks.forEach((chunk, index) => tags.push(`lead-${key.replace(/_/g, '-')}:${chunks.length > 1 ? `${index + 1}-of-${chunks.length}:` : ''}${chunk}`));
    }
    if (answers) for (const [key, value] of Object.entries(validateAnswers(answers))) tags.push(`lead-${key}:${value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '')}`);
    return tags.join(',');
  }
  class NativeLeadSession {
    constructor(storage, clock = Date.now) { this.storage = storage; this.clock = clock; }
    read() {
      try {
        const state = JSON.parse(this.storage.getItem(nativeSessionKey) || 'null');
        if (!state || this.clock() - state.updatedAt > 30 * 60000 || !['email', 'answers', null].includes(state.pending) || !/^[a-f0-9-]{36}$/i.test(state.payload.requestId)) return null;
        normaliseEmail(state.payload.email);
        if (state.pending === 'answers') validateAnswers(state.answers);
        return state;
      } catch (_) { return null; }
    }
    save(state) {
      state.updatedAt = this.clock();
      try { this.storage.setItem(nativeSessionKey, JSON.stringify(state)); }
      catch (_) { throw new Error('Allow temporary browser storage to continue this signup.'); }
      return state;
    }
    email(payload) {
      return this.save({ payload: { ...payload, email: normaliseEmail(payload.email) }, pending: 'email', accepted: false, answers: null });
    }
    answers(answers) {
      const state = this.read();
      if (!state?.accepted) throw new Error('Enter your email again.');
      return this.save({ ...state, answers: validateAnswers(answers), pending: 'answers' });
    }
    resume(returnedStep, posted) {
      const state = this.read();
      if (!state) return { step: 'email', state: null };
      if (posted && returnedStep === state.pending) {
        if (state.pending === 'answers' && state.accepted) {
          this.storage.removeItem(nativeSessionKey);
          return { step: 'code', state };
        }
        if (state.pending === 'email') {
          this.save({ ...state, accepted: true, pending: null });
          return { step: 'answers', state: this.read(), leadEventId: state.payload.requestId };
        }
      }
      return { step: state.accepted ? 'answers' : 'email', state };
    }
  }

  function sendMetaLead(id, win, doc) {
    if (typeof id !== 'string' || !/^[a-f0-9-]{36}$/i.test(id)) return false;
    function send() {
      try {
        const privacy = win.Shopify?.customerPrivacy;
        const choice = privacy?.currentVisitorConsent?.() || {};
        if (privacy?.marketingAllowed?.() !== true || privacy?.analyticsProcessingAllowed?.() !== true ||
            choice.marketing === 'no' || choice.analytics === 'no' || choice.sale_of_data === 'no' ||
            (typeof privacy.saleOfDataAllowed === 'function' && privacy.saleOfDataAllowed() !== true)) return false;
        const key = 'velven_meta_lead_sent:' + id;
        if (win.sessionStorage.getItem(key)) return false;
        // Claim before queueing so repeated readiness callbacks cannot emit twice.
        win.sessionStorage.setItem(key, '1');
        const pixelId = '1636443561176835';
        if (typeof win.fbq !== 'function') {
          const fbq = function () { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
          fbq.push = fbq;
          fbq.loaded = true;
          fbq.version = '2.0';
          fbq.queue = [];
          win.fbq = fbq;
          win._fbq = win._fbq || fbq;
          const script = doc.createElement('script');
          script.async = true;
          script.src = 'https://connect.facebook.net/en_US/fbevents.js';
          doc.head.appendChild(script);
        }
        if (!win.__velvenPopupMetaInitialized) {
          let initialized = false;
          try { initialized = win.fbq.getState?.()?.pixels?.some(pixel => String(pixel.id) === pixelId) === true; } catch (_) {}
          if (!initialized) {
            win.fbq('set', 'autoConfig', false, pixelId);
            win.fbq('init', pixelId);
          }
          win.__velvenPopupMetaInitialized = true;
        }
        // Scope the standard event to this dataset; do not broadcast to other pixels.
        win.fbq('trackSingle', pixelId, 'Lead', { content_name: 'welcome-popup' }, { eventID: id });
        return true;
      } catch (_) { return false; } // Measurement must never block the Shopify signup.
    }
    if (win.Shopify?.customerPrivacy) return send();
    try {
      win.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) send(); });
    } catch (_) { /* Unknown permission fails closed. */ }
    return false;
  }

  // PII-free funnel events. VelvenPostHog.capture enforces analytics consent and the /contact exclusion; absent means no-op.
  function track(win, name) {
    const capture = () => {
      try { win.VelvenPostHog?.capture?.(name); } catch (_) {}
    };
    try {
      if (win.Shopify?.customerPrivacy) capture();
      else win.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) capture(); });
    } catch (_) {}
  }

  // Simulation only: memory is deliberately discarded on navigation, never sent to a customer API or pixel.
  class PreviewLeadAdapter {
    constructor() {
      this.records = new Map();
      this.leadEvents = new Set();
    }
    async saveEmail(payload) {
      const email = normaliseEmail(payload.email);
      let record = [...this.records.values()].find(item => item.email === email);
      if (!record) {
        record = { ...payload, email, id: crypto.randomUUID(), project: null, timing: null, installer: null };
        this.records.set(record.id, record);
      }
      return { id: record.id };
    }
    async lead(id) {
      if (!this.records.has(id)) throw new Error('Enter your email again.');
      this.leadEvents.add(id);
    }
    async saveAnswers(id, answers) {
      const record = this.records.get(id);
      if (!record) throw new Error('Enter your email again.');
      Object.assign(record, validateAnswers(answers), { completedAt: new Date().toISOString() });
      return { code: 'WELCOME10' };
    }
  }

  class DriveLeadAdapter {
    constructor(endpoint, accessToken, fetchImpl = globalThis.fetch.bind(globalThis)) {
      if (!/^https:\/\/script\.google\.com\/macros\/s\/[a-zA-Z0-9_-]+\/exec$/.test(endpoint) || typeof accessToken !== 'string' || accessToken.length < 32) throw new Error('Configure the preview Drive endpoint and token.');
      this.endpoint = endpoint;
      this.accessToken = accessToken;
      this.fetchImpl = fetchImpl;
      this.requests = new Map();
      this.tokens = new Map();
    }
    async request(action, body) {
      // text/plain is a CORS simple request: Apps Script has no OPTIONS handler.
      const response = await this.fetchImpl(this.endpoint, {
        method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        credentials: 'omit', redirect: 'follow', cache: 'no-store',
        body: JSON.stringify({ ...body, action, accessToken: this.accessToken }),
        signal: AbortSignal.timeout(20000)
      });
      if (!response.ok) throw new Error('Could not save. Please try again.');
      const result = await response.json();
      if (result.ok !== true) throw new Error('Could not save. Please try again.');
      return result;
    }
    async saveEmail(payload) {
      const email = normaliseEmail(payload.email);
      if (!this.requests.has(email)) this.requests.set(email, payload.requestId || crypto.randomUUID());
      const result = await this.request('email', {
        email, sourcePage: payload.sourcePage, attribution: payload.attribution,
        consentVersion: payload.consentVersion, honeypot: payload.honeypot || '',
        requestId: this.requests.get(email)
      });
      if (typeof result.id !== 'string' || !/^[a-f0-9-]{36}$/i.test(result.id) || typeof result.token !== 'string' || result.token.length < 64) throw new Error('Could not confirm your signup. Please try again.');
      this.tokens.set(result.id, result.token);
      return { id: result.id };
    }
    async lead(id) {
      if (!this.tokens.has(id)) throw new Error('Enter your email again.');
      // Preview storage only: do not send a Meta/analytics Lead or any PII.
    }
    async saveAnswers(id, answers) {
      const result = await this.request('answers', { id, token: this.tokens.get(id), answers: validateAnswers(answers), honeypot: '' });
      if (result.code !== 'WELCOME10') throw new Error('Could not confirm your code. Please try again.');
      return result;
    }
  }

  function sourceDetails(url, referrer = '', storage) {
    let retained;
    try { retained = JSON.parse(storage?.getItem('velven_lead_preview_attribution') || 'null'); } catch (_) {}
    let previous;
    try {
      const candidate = new URL(referrer);
      if (candidate.origin === url.origin) previous = candidate;
    } catch (_) { /* A missing referrer has no campaign fields. */ }
    const attribution = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'campaign_id', 'adset_id', 'ad_id']) {
      const value = url.searchParams.get(key) || previous?.searchParams.get(key) || retained?.[key];
      if (typeof value === 'string' && value) attribution[key] = value.slice(0, 256).replace(/[\uD800-\uDBFF]$/, '');
    }
    try { storage?.setItem('velven_lead_preview_attribution', JSON.stringify(attribution)); } catch (_) {}
    return { signupAt: new Date().toISOString(), sourcePage: url.pathname.slice(0, 2048), attribution };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { PreviewLeadAdapter, DriveLeadAdapter, NativeLeadSession, nativeLeadTags, sendMetaLead, track, sourceDetails, choices, normaliseEmail, validateAnswers, consentVersion };
  let campaignStorage;
  if (typeof window !== 'undefined' && ['unpublished', 'main'].includes(window.Shopify?.theme?.role)) {
    try { campaignStorage = window.sessionStorage; } catch (_) {}
    sourceDetails(new URL(window.location.href), document.referrer, campaignStorage);
  }
  if (typeof customElements === 'undefined' || customElements.get('velven-lead-preview')) return;

  customElements.define('velven-lead-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.ready || (window.Shopify?.theme?.role !== 'unpublished' && this.dataset.nativeShopify !== 'true')) return;
      this.ready = true;
      const reviewUrl = new URL(window.location.href);
      if (reviewUrl.searchParams.get('lead_popup_reset') === '1') {
        try {
          localStorage.removeItem('velven_lead_preview_dismissed_at');
          localStorage.removeItem(this.closest('modal-dialog').id);
          sessionStorage.removeItem('velven_lead_preview_visit');
          sessionStorage.removeItem(nativeSessionKey);
          for (const key of ['velven_lead_preview_visit', 'velven_lead_preview_signed_up', 'velven_lead_signed_up', 'velven_has_purchased']) document.cookie = `${key}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
          reviewUrl.searchParams.delete('lead_popup_reset');
          history.replaceState(null, '', reviewUrl);
          const modal = this.closest('modal-dialog');
          if (typeof modal.scheduleLeadPreview === 'function') modal.scheduleLeadPreview();
        } catch (_) { /* Storage restrictions keep the popup suppressed. */ }
      }
      if (this.dataset.nativeShopify === 'true') {
        this.connectNative();
        return;
      }
      this.adapter = new PreviewLeadAdapter();
      this.emailForm = this.querySelector('[data-email-form]');
      this.answersForm = this.querySelector('[data-answers-form]');
      this.error = this.querySelector('[data-error]');
      this.querySelectorAll('[data-email-form] [disabled]').forEach(control => { control.disabled = false; });
      this.emailForm.addEventListener('submit', async event => {
        event.preventDefault();
        if (this.busy || !this.emailForm.reportValidity()) return;
        await this.save(this.emailForm, async () => {
          const result = await this.adapter.saveEmail({
            ...sourceDetails(new URL(window.location.href), document.referrer, campaignStorage),
            email: this.emailForm.querySelector('input').value,
            consentVersion,
            honeypot: this.emailForm.querySelector('[data-honeypot]').value
          });
          this.leadId = result.id;
          await this.adapter.lead(this.leadId);
          document.cookie = 'velven_lead_preview_signed_up=1; Max-Age=31536000; Path=/; SameSite=Lax; Secure';
          this.showStep('answers');
        });
      });
      this.answersForm.addEventListener('submit', async event => {
        event.preventDefault();
        if (this.busy || !this.answersForm.reportValidity()) return;
        await this.save(this.answersForm, async () => {
          const answers = Object.fromEntries(new FormData(this.answersForm));
          const result = await this.adapter.saveAnswers(this.leadId, answers);
          this.querySelector('[data-code]').textContent = result.code;
          this.showStep('code');
        });
      });
      this.querySelector('[data-back]').addEventListener('click', () => {
        if (!this.busy) this.showStep('email');
      });
    }
    connectNative() {
      this.emailForm = this.querySelector('[data-email-form]');
      this.answersForm = this.querySelector('[data-answers-form]');
      this.error = this.querySelector('[data-error]');
      const fail = error => { this.error.textContent = error.message || 'Could not continue. Please try again.'; this.error.hidden = false; };
      let session;
      try { session = new NativeLeadSession(window.sessionStorage); }
      catch (_) { fail(new Error('Allow temporary browser storage to continue this signup.')); return; }
      this.querySelectorAll('[data-email-form] [disabled]').forEach(control => { control.disabled = false; });
      const modal = this.closest('modal-dialog');
      const markViewed = () => {
        try {
          if (!modal.hasAttribute('open') || window.sessionStorage.getItem('velven_lead_popup_viewed')) return;
          window.sessionStorage.setItem('velven_lead_popup_viewed', '1');
          track(window, 'lead_popup_viewed');
        } catch (_) {}
      };
      if (typeof MutationObserver === 'function') new MutationObserver(markViewed).observe(modal, { attributes: true, attributeFilter: ['open'] });
      markViewed();
      const prepare = (form, state, step) => {
        form.querySelector('[name="contact[email]"]').value = state.payload.email;
        form.querySelector('[name="contact[tags]"]').value = nativeLeadTags(state.payload, step === 'answers' ? state.answers : null);
        const target = new URL(window.location.href);
        target.search = '';
        if (window.Shopify.theme.role === 'unpublished') target.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
        target.searchParams.set('lead_popup_return', step);
        // Shopify can use return_to verbatim instead of appending its default success query.
        target.searchParams.set('customer_posted', 'true');
        target.hash = form.id;
        form.querySelector('[name="return_to"]').value = target.pathname + target.search + target.hash;
      };
      // Do not prevent a valid native submit: Shopify owns hCaptcha and its redirect.
      this.emailForm.addEventListener('submit', event => {
        try {
          if (this.emailForm.querySelector('[data-honeypot]').value) throw new Error('Could not continue.');
          const state = session.email({ ...sourceDetails(new URL(window.location.href), document.referrer, campaignStorage), email: this.emailForm.querySelector('[name="contact[email]"]').value, requestId: crypto.randomUUID(), consentVersion });
          prepare(this.emailForm, state, 'email');
          track(window, 'lead_popup_submitted');
        } catch (error) { event.preventDefault(); fail(error); }
      });
      this.answersForm.addEventListener('submit', event => {
        try {
          const answers = Object.fromEntries(Object.keys(choices).map(key => [key, this.answersForm.querySelector(`[name="${key}"]:checked`)?.value]));
          prepare(this.answersForm, session.answers(answers), 'answers');
        } catch (error) { event.preventDefault(); fail(error); }
      });
      this.querySelector('[data-back]').addEventListener('click', () => this.showStep('email'));
      const url = new URL(window.location.href);
      const returned = url.searchParams.get('lead_popup_return') ||
        (url.hash === '#' + this.emailForm.id ? 'email' : url.hash === '#' + this.answersForm.id ? 'answers' : null);
      const posted = url.searchParams.get('customer_posted') === 'true' || Boolean(this.querySelector('[data-native-success="true"]'));
      const result = session.resume(returned, posted);
      if (result.leadEventId) {
        sendMetaLead(result.leadEventId, window, document);
        track(window, 'lead_popup_confirmed');
      }
      if (result.state) {
        this.emailForm.querySelector('[name="contact[email]"]').value = result.state.payload.email;
        for (const [key, value] of Object.entries(result.state.answers || {})) {
          const input = [...this.answersForm.querySelectorAll(`[name="${key}"]`)].find(input => input.value === value);
          if (input) input.checked = true;
        }
      }
      if (result.step === 'code') {
        this.querySelector('[data-code]').textContent = 'WELCOME10';
        document.cookie = 'velven_lead_preview_signed_up=1; Max-Age=31536000; Path=/; SameSite=Lax; Secure';
      }
      if (result.state && (returned || (result.state.accepted && result.state.payload.sourcePage === url.pathname))) {
        this.showStep(result.step);
        if (returned && !posted) fail(new Error(this.querySelector('[data-native-errors]')?.textContent.trim() || 'Shopify has not confirmed this step. Please try again.'));
        customElements.whenDefined('modal-dialog').then(() => this.closest('modal-dialog').show(undefined, true));
        if (posted) {
          url.searchParams.delete('customer_posted');
          url.searchParams.delete('lead_popup_return');
          url.hash = '';
          history.replaceState(null, '', url);
        }
      }
      else if (result.state?.pending === 'email' && !result.state.accepted && !returned && !posted) {
        // Step 1 was submitted but Shopify never confirmed it (e.g. abandoned hCaptcha): reopen at step 1.
        this.showStep('email');
        fail(new Error('Finish Shopify verification to get your code.'));
        customElements.whenDefined('modal-dialog').then(() => this.closest('modal-dialog').show(undefined, true));
      }
      if (posted && result.state && this.dataset.driveEndpoint) {
        // Optional copy only: a Sheet outage must never block a native Shopify signup.
        Promise.resolve().then(async () => {
          const adapter = new DriveLeadAdapter(this.dataset.driveEndpoint, this.dataset.driveToken);
          const saved = await adapter.saveEmail({ ...result.state.payload, honeypot: '' });
          if (result.step === 'code') await adapter.saveAnswers(saved.id, result.state.answers);
        }).catch(() => {
          const note = this.querySelector('[data-storage-note]');
          note.textContent = 'Shopify accepted this step. Optional Google Sheet copy was not confirmed.';
          note.hidden = false;
        });
      }
    }
    async save(form, action) {
      this.busy = true;
      this.error.hidden = true;
      const button = form.querySelector('[type="submit"]');
      const label = button.textContent;
      button.disabled = true;
      button.textContent = 'Saving…';
      form.setAttribute('aria-busy', 'true');
      try {
        await action();
      } catch (error) {
        this.error.textContent = error.message || 'Could not save. Please try again.';
        this.error.hidden = false;
      } finally {
        button.disabled = false;
        button.textContent = label;
        form.removeAttribute('aria-busy');
        this.busy = false;
      }
    }
    showStep(step) {
      this.querySelectorAll('[data-step]').forEach(panel => { panel.hidden = panel.dataset.step !== step; });
      const heading = this.querySelector('[data-heading]');
      heading.textContent = { email: '10% off your first order', answers: 'Your project', code: 'Your code' }[step];
      this.querySelector('[data-progress]').textContent = { email: '1 of 2', answers: '2 of 2', code: 'Done' }[step];
      this.error.hidden = true;
      heading.focus();
      const dialog = this.closest('[role="dialog"]');
      if (dialog) dialog.scrollTop = 0;
    }
  });
})();
